import type { ReactNode } from 'react';
import { Internal, Mail, Pending, ReadinessNotice } from '../components/legal';
import {
  ButtonLink,
  Card,
  Eyebrow,
  Heading,
  Lead,
  Section,
} from '../components/ui';
import { COMPANY, PAYMENTS, SELLER_NOTICE } from '../lib/company';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * Buyer support.
 *
 * Separate from the Contact page on purpose. Contact is a sales enquiry form
 * for people deciding whether to buy; this page is for people who have already
 * paid and need something fixed, and it is where buyer support details live —
 * reachable from navigation, with an email
 * address and a telephone number.
 *
 * Billing questions come to us. We sell directly and the payment gateway is a
 * processor rather than a reseller, so there is no third party holding the
 * order — which means this page must not read as though there were one. The
 * card statement is the exception worth calling out: it may carry the
 * processor's name, and an unrecognised line on a statement is the single most
 * common reason a buyer reaches for a chargeback instead of an email.
 */

interface Channel {
  title: string;
  description: ReactNode;
  contact: ReactNode;
  target: string;
}

const CHANNELS: Channel[] = [
  {
    title: 'Product and account support',
    description: (
      <>
        Agent runs, credentials, reports, plan entitlements and anything that is not behaving
        the way the documentation says it should.
      </>
    ),
    contact: <Mail address={COMPANY.email.support} />,
    target: 'First reply within 1 business day',
  },
  {
    title: 'Sales, enterprise and professional services',
    description: (
      <>
        Premium and enterprise plans, private deployment, the one-time setup engagement and
        the monthly support retainer.
      </>
    ),
    contact: <Mail address={COMPANY.email.sales} />,
    target: 'First reply within 2 business days',
  },
  {
    title: 'Security disclosure',
    description: (
      <>
        Report a suspected vulnerability privately here rather than through any public form.
        Please do not test against other customers.
      </>
    ),
    contact: <Mail address={COMPANY.email.security} />,
    target: 'Acknowledged within 2 business days',
  },
];

export function Support() {
  usePageTitle('Support — WaveQA');

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>Support</Eyebrow>
        <Heading level={1} className="text-white">
          Getting help
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          How to reach a person about {COMPANY.product}, and where to go for anything to do
          with a payment. We sell directly, so billing is ours to answer for — there is no
          reseller to be handed off to.
        </Lead>

        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm">
          <p className="text-hull-300">
            <span className="text-hull-500">Email</span>{' '}
            <Mail address={COMPANY.email.support} />
          </p>
          <p className="text-hull-300">
            <span className="text-hull-500">Telephone</span>{' '}
            {COMPANY.supportPhone ? (
              <a
                href={`tel:${COMPANY.supportPhone.replace(/[^+\d]/g, '')}`}
                className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
              >
                {COMPANY.supportPhone}
              </a>
            ) : (
              <Pending>support telephone number</Pending>
            )}
          </p>
        </div>
      </Section>

      <Section className="border-b border-hull-800">
        <ReadinessNotice />

        <Heading className="text-white">Where to write</Heading>
        <ul className="mt-8 grid gap-4 lg:grid-cols-3">
          {CHANNELS.map((channel) => (
            <Card as="li" key={channel.title} className="flex flex-col">
              <h3 className="text-base font-semibold text-white">{channel.title}</h3>
              <p className="mt-2.5 flex-1 text-sm leading-relaxed text-hull-300">
                {channel.description}
              </p>
              <p className="mt-5 text-sm">{channel.contact}</p>
              <p className="mt-1.5 text-xs text-hull-500">{channel.target}</p>
            </Card>
          ))}
        </ul>
        <p className="mt-5 text-xs leading-relaxed text-hull-400">
          Response targets are aims, not contractual commitments. A guaranteed response time
          is available only where an SLA is agreed in writing on a Premium or enterprise
          engagement.
        </p>
      </Section>

      <Section className="border-b border-hull-800">
        <Heading className="text-white">Payments, invoices and refunds</Heading>
        <Lead className="mt-4 max-w-2xl">{SELLER_NOTICE}</Lead>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Card>
            <h3 className="text-base font-semibold text-white">Write to us about</h3>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-hull-300">
              {[
                'A copy of an invoice or receipt',
                'Identifying an unfamiliar charge on your statement',
                'Updating the card or billing details on a subscription',
                'Requesting a refund, or cancelling a renewal',
                'Adding a tax number to an invoice',
              ].map((item) => (
                <li key={item} className="flex gap-2.5">
                  <span aria-hidden="true" className="mt-px text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <ButtonLink to="/refund-policy" variant="secondary">
                Refund Policy
              </ButtonLink>
            </div>
            <p className="mt-3 text-xs text-hull-400">
              Email <Mail address={COMPANY.email.support} /> quoting the order reference on
              your receipt.
            </p>
          </Card>

          <Card>
            <h3 className="text-base font-semibold text-white">
              What appears on your statement
            </h3>
            <p className="mt-2.5 text-sm leading-relaxed text-hull-300">
              Payments are taken in {PAYMENTS.currency} and processed by{' '}
              {PAYMENTS.gatewayName ?? <Pending>payment gateway</Pending>}. Your card
              statement may therefore show the processor's name rather than{' '}
              {COMPANY.product}, which is the usual reason a charge looks unfamiliar.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-hull-300">
              Please come to us before raising a chargeback with your bank. A chargeback takes
              weeks and freezes the account while it is investigated, where a direct request
              under our <Internal to="/refund-policy">Refund Policy</Internal> is usually
              settled the same day.
            </p>
          </Card>
        </div>
      </Section>

      <Section className="border-b border-hull-800">
        <Heading className="text-white">Making a report we can act on</Heading>
        <Lead className="mt-4 max-w-2xl">
          {COMPANY.product} is built on the idea that a verdict is worth nothing without the
          evidence behind it. The same is true of a bug report.
        </Lead>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: 'The account',
              detail: 'The email address on the account, and the plan you are on.',
            },
            {
              title: 'The run',
              detail:
                'The run or report identifier, and roughly when it happened. That is usually enough for us to find the trace.',
            },
            {
              title: 'What you expected',
              detail:
                'What you thought would happen, and what happened instead. Both halves — the second alone is rarely enough.',
            },
            {
              title: 'Nothing secret',
              detail:
                'No passwords, API keys or agent credentials in the message. If one has been exposed, rotate it and tell us.',
            },
          ].map((item) => (
            <Card as="li" key={item.title}>
              <h3 className="text-sm font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">{item.detail}</p>
            </Card>
          ))}
        </ul>
      </Section>

      <Section>
        <Heading className="text-white">Before you write</Heading>
        <Lead className="mt-4 max-w-2xl">
          A fair number of questions are already answered somewhere.
        </Lead>
        <div className="mt-7 flex flex-wrap gap-3">
          <ButtonLink to="/docs" variant="secondary">
            Documentation
          </ButtonLink>
          <ButtonLink to="/pricing" variant="secondary">
            Plans and pricing
          </ButtonLink>
          <ButtonLink to="/refund-policy" variant="secondary">
            Refund Policy
          </ButtonLink>
          <ButtonLink to="/terms" variant="secondary">
            Terms of Service
          </ButtonLink>
          <ButtonLink to="/privacy" variant="secondary">
            Privacy Policy
          </ButtonLink>
        </div>
        <p className="mt-6 text-sm text-hull-400">
          Not a customer yet, and wondering whether {COMPANY.product} fits?{' '}
          <Internal to="/contact">Talk to us</Internal> instead. Postal enquiries can be
          addressed to{' '}
          {COMPANY.registeredAddress ?? <Pending>registered address</Pending>}.
        </p>
      </Section>
    </>
  );
}
